import { useSyncExternalStore } from 'react';
import { createPortal } from 'react-dom';
import { toastStore } from './store';
import { Toast } from './Toast';

/**
 * Renders the toast dock through a portal into `document.body` so it floats
 * above every screen. Despite the name it provides no React context — it
 * subscribes to the module-level `toastStore` and is mounted once as a sibling
 * of the app (see `main.tsx`). The store is what the rest of the app talks to,
 * via the `toast` singleton (`toast/useToast.ts`) or the `lib/toast.ts` helpers.
 *
 * Placement defaults to bottom-centre; `placement="right"` docks bottom-right.
 * Newest toast renders nearest the top of the stack.
 */

type Placement = 'center' | 'right';

export function ToastProvider({ placement = 'center' }: { placement?: Placement } = {}) {
  const toasts = useSyncExternalStore(
    toastStore.subscribe,
    toastStore.getSnapshot,
    toastStore.getSnapshot,
  );

  if (typeof document === 'undefined') return null;

  const dock = (
    <div
      style={{
        position: 'fixed',
        bottom: 22,
        zIndex: 60,
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        pointerEvents: 'none',
        ...(placement === 'right'
          ? { right: 22, left: 'auto', alignItems: 'flex-end' }
          : { left: 0, right: 0, alignItems: 'center' }),
      }}
    >
      {/* Newest on top: reverse so the most recent pill sits nearest the top. */}
      {[...toasts].reverse().map((t) => (
        <div key={t.id} style={{ pointerEvents: 'auto' }}>
          <Toast
            id={t.id}
            tone={t.tone}
            message={t.message}
            dur={t.dur}
            onClose={toastStore.remove}
          />
        </div>
      ))}
    </div>
  );

  return createPortal(dock, document.body);
}
